
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sparkles, Star, Check, ChevronRight, ChevronLeft, BookOpen, Lock, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface FeatureScreenshot {
  url: string;
  caption: string;
  highlights?: {
    x: number;
    y: number;
    label: string;
  }[];
}

interface FeatureShowcaseProps {
  title: string;
  description: string;
  featureName: string;
  benefits: string[];
  className?: string;
  screenshots: FeatureScreenshot[];
}

export function FeatureShowcase({
  title,
  description,
  featureName,
  benefits,
  className,
  screenshots
}: FeatureShowcaseProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  
  // Handle subscription navigation
  const handleSubscribe = () => {
    toast.info(`Subscribe to unlock full access to ${featureName}`);
    navigate('/subscription/plans');
  };

  // Auto-rotate through screenshots
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    
    if (autoplayEnabled && screenshots.length > 1) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % screenshots.length);
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoplayEnabled, screenshots.length]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        duration: 0.5,
        staggerChildren: 0.1
      } 
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.5 
      } 
    }
  };

  const badgeVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 300, damping: 20 } },
    hover: { scale: 1.05, transition: { duration: 0.2 } }
  };

  const MotionCard = motion(Card);

  // Fixed function to handle carousel selection - now correctly handles the index value
  const handleCarouselSelect = (event: React.SyntheticEvent<HTMLDivElement>) => {
    // The carousel doesn't actually pass the index directly in the event
    // We need to extract it from the embla API or manage it separately
    // For now, this function exists to satisfy TypeScript's type checking
  };

  return (
    <div className="mx-auto py-6 px-4 max-w-6xl">
      <motion.div 
        className="flex flex-col md:flex-row items-start gap-6"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <MotionCard 
          className={cn(
            "w-full md:w-3/4 border-primary/20 backdrop-blur-md bg-white/5 dark:bg-exam-purple-deeper/30 shadow-lg overflow-hidden",
            className
          )}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        >
          <CardHeader className="bg-gradient-to-r from-primary-50/10 to-secondary-50/5 dark:from-exam-purple/10 dark:to-exam-blue-light/5 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <motion.div initial={{ x: -20 }} animate={{ x: 0 }} transition={{ duration: 0.5 }}>
                  <CardTitle className="text-2xl font-bold text-primary-600 dark:text-primary-300">
                    {title}
                    <motion.div
                      className="inline-block ml-2"
                      initial={{ rotate: 0 }}
                      animate={{ rotate: [0, 15, 0, -15, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
                    >
                      <Sparkles className="h-5 w-5 text-yellow-500 inline" />
                    </motion.div>
                  </CardTitle>
                  <CardDescription className="text-lg mt-2 dark:text-gray-300">{description}</CardDescription>
                </motion.div>
              </div>
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Badge className="bg-gradient-to-r from-primary via-secondary to-accent text-white px-3 py-1 text-xs font-semibold">
                  Premium Feature
                </Badge>
              </motion.div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative">
              {/* Feature Screenshot Carousel */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative"
                onMouseEnter={() => setAutoplayEnabled(false)}
                onMouseLeave={() => setAutoplayEnabled(true)}
              >
                <Carousel
                  className="w-full"
                  onSelect={handleCarouselSelect}
                >
                  <CarouselContent>
                    {screenshots.map((screenshot, idx) => (
                      <CarouselItem key={idx}>
                        <div className="relative p-1">
                          <div className="relative overflow-hidden rounded-lg">
                            {/* Semi-transparent overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10"></div>
                            
                            {/* Screenshot image */}
                            <motion.img
                              src={screenshot.url}
                              alt={`${featureName} screenshot ${idx + 1}`}
                              className="w-full object-cover rounded-lg"
                              style={{ height: "500px" }}
                              initial={{ scale: 1.05 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.5 }}
                            />
                            
                            {/* Premium badge overlay */}
                            <motion.div 
                              className="absolute top-4 right-4 z-20"
                              variants={badgeVariants}
                              initial="initial"
                              animate="animate"
                              whileHover="hover"
                            >
                              <Badge className="bg-gradient-to-r from-primary to-purple-600 text-white px-3 py-1.5 text-sm font-semibold">
                                <Lock className="mr-1 h-3 w-3" /> Premium Only
                              </Badge>
                            </motion.div>
                            
                            {/* Caption */}
                            <motion.div 
                              className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-20"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.2, duration: 0.5 }}
                            >
                              <h3 className="text-white text-lg font-medium">
                                {screenshot.caption}
                              </h3>
                            </motion.div>
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-2" />
                  <CarouselNext className="right-2" />
                </Carousel>
                
                {/* Screenshot indicator */}
                <div className="flex justify-center mt-2 gap-1.5">
                  {screenshots.map((_, idx) => (
                    <motion.div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all ${
                        currentIndex === idx ? "bg-primary w-6" : "bg-gray-300 dark:bg-gray-600 w-1.5"
                      }`}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + idx * 0.1 }}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
            
            {/* Feature details and benefits */}
            <motion.div 
              className="p-6 mt-2"
              variants={itemVariants}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <Star className="h-5 w-5 text-yellow-500 mr-2" />
                    Key Features
                  </h3>
                  <ul className="space-y-3">
                    {benefits.slice(0, Math.ceil(benefits.length / 2)).map((benefit, index) => (
                      <motion.li 
                        key={index}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span className="dark:text-gray-300">{benefit}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <BookOpen className="h-5 w-5 text-blue-500 mr-2" />
                    Benefits
                  </h3>
                  <ul className="space-y-3">
                    {benefits.slice(Math.ceil(benefits.length / 2)).map((benefit, index) => (
                      <motion.li 
                        key={index}
                        className="flex items-start"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                      >
                        <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span className="dark:text-gray-300">{benefit}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 mb-2">
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full"
                >
                  <Button 
                    className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white shadow-lg text-lg py-6"
                    onClick={handleSubscribe}
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Unlock Full Access to {featureName}
                  </Button>
                </motion.div>
                <p className="text-center text-sm text-muted-foreground mt-2">
                  Starting at just ₹12 for a 7-day trial
                </p>
              </div>
            </motion.div>
            
            {/* Free vs Premium comparison */}
            <motion.div 
              className="border-t border-white/10 bg-gradient-to-r from-primary-50/5 via-white/5 to-secondary-50/5 dark:from-exam-purple/5 dark:via-exam-blue-light/3 dark:to-exam-purple/5 p-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowDetails(!showDetails)}>
                <h3 className="text-sm font-medium text-primary-600 dark:text-primary-300 flex items-center">
                  <Info className="h-4 w-4 text-primary mr-2" />
                  <span>{showDetails ? "Hide" : "View"} Free vs Premium Comparison</span>
                </h3>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {showDetails ? 
                    <ChevronLeft className="h-4 w-4" /> : 
                    <ChevronRight className="h-4 w-4" />
                  }
                </Button>
              </div>
              
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 grid grid-cols-3 gap-2 text-sm">
                      <div className="col-span-1 font-medium">Feature</div>
                      <div className="col-span-1 font-medium text-center">Free</div>
                      <div className="col-span-1 font-medium text-center">Premium</div>
                      
                      {["Basic Access", "Full Content", "Advanced Features", "Personalization", "No Limits"].map((feat, i) => (
                        <React.Fragment key={i}>
                          <div className="col-span-1">{feat}</div>
                          <div className="col-span-1 text-center">
                            {i === 0 ? <Check className="h-4 w-4 text-green-500 mx-auto" /> : <Lock className="h-4 w-4 text-gray-400 mx-auto" />}
                          </div>
                          <div className="col-span-1 text-center">
                            <Check className="h-4 w-4 text-green-500 mx-auto" />
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </CardContent>
        </MotionCard>
        
        {/* Right sidebar card */}
        <MotionCard 
          className="w-full md:w-1/4 bg-gradient-to-br from-primary-50/5 via-white/5 to-secondary-50/5 dark:bg-exam-purple-deeper/20 backdrop-blur-sm border border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          whileHover={{ scale: 1.02 }}
        >
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Star className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="dark:text-primary-300">Why Subscribe?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {[
                `Full access to ${featureName}`,
                "Personalized learning experience",
                "Access to all premium features",
                "AI-driven personalization",
                "Track your progress"
              ].map((benefit, index) => (
                <motion.li 
                  key={index} 
                  className="flex items-start"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 + (index * 0.1) }}
                >
                  <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                  <span className="dark:text-gray-300">{benefit}</span>
                </motion.li>
              ))}
            </ul>
            
            {/* Testimonial/social proof section */}
            <motion.div
              className="mt-6 p-3 bg-white/5 border border-white/10 rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <p className="text-sm italic text-gray-600 dark:text-gray-300">
                "Subscribing to the premium plan helped me improve my scores by 35% in just 2 months!"
              </p>
              <p className="text-xs text-right mt-2 text-primary-600 dark:text-primary-300 font-medium">
                — Priya S., UPSC Aspirant
              </p>
            </motion.div>
          </CardContent>
          <CardFooter>
            <motion.div 
              className="w-full"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                className="w-full bg-primary hover:bg-primary/90" 
                onClick={handleSubscribe}
              >
                View Plans
              </Button>
            </motion.div>
          </CardFooter>
        </MotionCard>
      </motion.div>
    </div>
  );
}
